import React, { useEffect, useState } from "react";

import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { hasAuthHint } from "../utils/authHint";
import AuthLoadingScreen from "./AuthLoadingScreen";

// Longest we'll hold a probably-signed-in visitor on the spinner before
// showing the login form anyway. If the pending auth check later resolves
// signed-in, the <Navigate> below still fires — the wait is a courtesy to
// avoid flashing a login form at returning users, not a correctness gate.
const MAX_SIGNED_IN_WAIT_MS = 2500;

export default function AuthProtectedNoUseRoute({ children }) {
  const { currentUser, authState } = useAuth();
  // Read once on mount: the hint only changes via this app's own auth flow,
  // and re-reading mid-render could flip the guard while the check is in
  // flight.
  const [likelySignedIn] = useState(hasAuthHint);
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  useEffect(() => {
    if (!likelySignedIn) return;
    const timer = setTimeout(() => setWaitedTooLong(true), MAX_SIGNED_IN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [likelySignedIn]);

  // Already signed in — send them to the dashboard rather than the login form.
  if (currentUser) {
    return <Navigate to="/dashboard" />;
  }

  // The auth check exists here only to redirect already-signed-in users away
  // from the login form. For a visitor with no sign-in hint (the common case
  // for this page), waiting on it — potentially through a 15-30s Cloud Run
  // cold start — just delays the form they came for. Render immediately; if
  // the check surprises us with a user, the redirect above takes over.
  if (
    authState?.status === "pending" &&
    likelySignedIn &&
    !waitedTooLong
  ) {
    return <AuthLoadingScreen />;
  }

  return children;
}
